import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import appManager from '@app/appManager';
import {
  ServiceSessionEventType,
  ServiceSessionOfferedEvent,
} from '../../types/ServiceEvents';
import requireUser from '../../lib/requireUser';
import log from '../../lib/log';
import { getSession, isOfferedSessionDeleted } from '../../lib/sessions';
import { serviceState } from '../../lib/state';
import getTenantId from '../../lib/getTenantId';
import isValidUser from '../../lib/isValidUser';
import { EventAvailabilityState } from '../../lib/eventAvailabilityState';
import { ServiceUserUtils } from '../../lib/serviceUserUtils';
import getQueue from '../../lib/getQueue';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';
import getLiveServicesTenantId from '../../lib/getLiveServicesTenantId';

/**
 * The offer event indicates that a session is being offered to
 * the recipient from a queue.
 * @param event
 */
async function handleAgentSessionOffered(
  event: Telecom.AgentSessionOfferedEvent,
) {
  // Track RTCS event latency - CRITICAL for diagnosing ringing delays
  trackRTCSEventLatency(event.timestamp, RTCSEventType.SESSION_OFFERED);

  if (!isValidUser(event.agentId)) {
    return;
  }

  const user = await requireUser(event.agentId);
  log('rtcs:agent:session_offered:' + user.name, event);

  // check if the session is deleted
  if (isOfferedSessionDeleted(event.conversationSid, event.timestamp)) {
    // event was received or processed out of order
    console.warn('offered session is already deleted', event.conversationId);
    return;
  }

  user.stats = userStatsFromAgentStats(event.stats);

  ServiceUserUtils.setEventActivityStatus(
    user,
    EventAvailabilityState.CallOffer,
  );

  // let's map the tenant id for Live Services
  const tenantId = serviceState.isLiveServicesNetwork
    ? getLiveServicesTenantId(
        event.tenantId,
        event.target.address,
        serviceState.liveServicesMappings,
      )
    : getTenantId(event.tenantId);

  // store the session if it doesn't exist, as session information is needed for
  // phoneCallPlaced handler.
  // this could happen when the session was created before user logged in,
  // so the `service:session:created` event was not handled.
  let session = getSession(event.conversationId);
  if (!session) {
    console.warn(
      'offered session was not stored previously',
      event.conversationId,
    );
    serviceState.sessions[event.conversationId] = {
      ...event,
      createdAt: Date.now(),
      tenantId,
    };
  }

  // update the session with queue and agent information so that it can be applied to the task
  session = serviceState.sessions[event.conversationId];
  session.queueId = event.queueId;
  session.queueName = event.queueName;
  session.queueEnterTime = event.queueEnterTime;
  session.target = event.target;
  session.tenantId = tenantId;

  // Calculate queueTimeout based on queue's rolloverSettings
  let queueTimeout: number | undefined;
  if (event.queueId && event.queueEnterTime) {
    const queue = getQueue(event.queueId);
    if (queue?.rolloverSettings?.timeout) {
      try {
        const enterTime = new Date(event.queueEnterTime).getTime();
        // Parse "hh:mm:ss" format
        const timeParts = queue.rolloverSettings.timeout.split(':').map(Number);
        if (timeParts.length === 3 && timeParts.every((part) => !isNaN(part))) {
          const [hours, minutes, seconds] = timeParts;
          const timeoutSeconds = hours * 3600 + minutes * 60 + seconds;
          if (!isNaN(enterTime) && timeoutSeconds > 0) {
            queueTimeout = enterTime + timeoutSeconds * 1000;
            session.queueTimeout = queueTimeout;
          }
        }
      } catch (error) {
        console.error('Failed to calculate queue timeout:', error);
      }
    }
  }

  appManager.events.emit<ServiceSessionOfferedEvent>(
    ServiceSessionEventType.OFFERED,
    {
      sessionId: event.conversationId,
      sessionSid: event.conversationSid,
      agentId: event.agentId,
      userId: user.id,
      from: event.source.address,
      to: event.target.address,
      displayName: event.source.displayName ?? '',
      tenantId,
      queueId: event.queueId,
      queueName: event.queueName,
      queueEnterTime: event.queueEnterTime,
      queueTimeout,
      source: event.source,
    },
  );
}

export default handleAgentSessionOffered;
